import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/authStore';
import { firstAllowedPath, type DashboardConfig } from '@/features/dashboard/config';
import { fmtInt } from '@/features/monopoly/utils';
import { KpiTile, Panel } from '@/features/monopoly/components/Primitives';
import { AllocationsPanel } from '@/features/monopoly/components/AllocationsPanel';
import { WinnerNameDialog } from '@/features/monopoly/components/WinnerNameDialog';
import { buildWinnersCsv, csvFileName, downloadCsv } from '@/features/monopoly/winnersCsv';
import {
  getAllCollectionWinners,
  getCollectionWinners,
  getLotteryWinners,
  proposeWinnerName,
  type CollectionWinners,
  type LotteryWinners,
  type LotteryWinner,
  type WinnersQuery,
  type WinnersTab,
} from '@/features/monopoly/winners';
import '@/features/monopoly/monopoly.css';

/*
 * "Gagnants et attribution des lots" — a PAGE of its own, not a dashboard tab.
 *
 * The dashboard answers "how is the campaign going"; this answers "who has won
 * and what happens next", which is operational work with its own audience,
 * refresh rate and (soon) actions. It therefore has its own route, its own
 * header, and a link back rather than a seat in the tab bar.
 *
 * Data is LIVE from the prize service, paged server-side — see features/monopoly/
 * winners.ts. Nothing here is pseudo-fresh or frozen.
 */

const PAGE_SIZE = 25;

const TABS: Array<{ id: WinnersTab; label: string }> = [
  { id: 'collections', label: 'Gagnants par collection' },
  { id: 'lottery', label: 'Gagnants du tirage' },
];

const COLLECTION_STATUS_LABELS: Record<string, string> = {
  all: 'Tous les statuts',
  awaiting: 'En attente d’envoi',
  reported: 'Envoyé pour attribution',
};

const LOTTERY_STATUS_LABELS: Record<string, string> = {
  all: 'Tous les statuts',
  pending: 'À valider',
  approved: 'Validé',
};

/** Tab and filters live in the URL, so a filtered list is a shareable link. */
function useWinnersState() {
  const read = () => {
    const p = new URLSearchParams(window.location.search);
    const tab = p.get('tab') === 'lottery' ? 'lottery' : 'collections';
    return {
      tab: tab as WinnersTab,
      page: Math.max(1, Number(p.get('page')) || 1),
      status: p.get('status') ?? 'all',
      collection: p.get('collection'),
      kind: p.get('kind'),
      from: p.get('from'),
      to: p.get('to'),
    };
  };
  const [state, setState] = useState(read);
  useEffect(() => {
    const onPop = () => setState(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const update = useCallback((next: Partial<ReturnType<typeof read>>) => {
    setState((prev) => {
      // Any change of tab or filter puts you back on page 1: page 4 of the old
      // filter is rarely page 4 of the new one, and an empty page reads as "no
      // winners" rather than "you are past the end".
      const merged = { ...prev, ...next, page: next.page ?? 1 };
      const p = new URLSearchParams();
      p.set('tab', merged.tab);
      if (merged.page > 1) p.set('page', String(merged.page));
      if (merged.status !== 'all') p.set('status', merged.status);
      if (merged.collection) p.set('collection', merged.collection);
      if (merged.kind && merged.kind !== 'all') p.set('kind', merged.kind);
      if (merged.from) p.set('from', merged.from);
      if (merged.to) p.set('to', merged.to);
      window.history.pushState(null, '', `${window.location.pathname}?${p.toString()}`);
      return merged;
    });
  }, []);
  return [state, update] as const;
}

export function MonopolyWinnersPage({ config }: { config: DashboardConfig }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [state, update] = useWinnersState();

  const query: WinnersQuery = useMemo(
    () => ({ page: state.page, limit: PAGE_SIZE, status: state.status, collection: state.collection, kind: state.kind, from: state.from, to: state.to }),
    [state.page, state.status, state.collection, state.kind, state.from, state.to],
  );

  const collections = useQuery({
    queryKey: ['monopoly-winners', 'collections', config.reportId, query],
    queryFn: () => getCollectionWinners(config.reportId, config.apiBaseUrl, query),
    enabled: state.tab === 'collections',
    retry: false,
  });
  const lottery = useQuery({
    queryKey: ['monopoly-winners', 'lottery', config.reportId, query],
    queryFn: () => getLotteryWinners(config.reportId, config.apiBaseUrl, query),
    enabled: state.tab === 'lottery',
    retry: false,
  });

  // Same allow-list guard as the dashboard: signed in, but this report isn't in
  // the token → bounce to the first one that is.
  const allowed = auth?.allowedReports ?? [];
  if (allowed.length > 0 && !allowed.includes(config.reportId)) {
    const target = firstAllowedPath(allowed);
    if (target && target !== config.path) return <Navigate to={target} replace />;
  }

  const active = state.tab === 'lottery' ? lottery : collections;
  const total = active.data?.meta.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mono-root">
      <div className="mono-app">
        <header className="mono-header">
          <div className="mono-header-row">
            <div className="mono-brand">
              <div className="mono-brand-mark" aria-hidden="true">
                M
              </div>
              <div>
                <h1 className="mono-brand-title">Gagnants et attribution des lots</h1>
                <div className="mono-brand-sub">Le Grand Jeu Monopoly 2026 · collections terminées et tirages carte chance</div>
              </div>
            </div>
            <div className="mono-header-actions">
              <button type="button" className="mono-signout" onClick={() => navigate(config.path)}>
                ← Tableau de bord
              </button>
            </div>
          </div>
          <div className="mono-tabs" role="tablist" aria-label="Listes de gagnants">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={state.tab === t.id}
                className="mono-tab"
                onClick={() => update({ tab: t.id, status: 'all', collection: null })}
              >
                {t.label}
              </button>
            ))}
          </div>
        </header>

        <main className="mono-main">
          {active.isError && (
            <Panel title="Liste indisponible">
              <p className="mono-note">
                {String((active.error as Error)?.message ?? '').includes('404')
                  ? "Ce rapport ne publie pas de liste de gagnants — vérifiez que le service des lots est configuré dans l'administration."
                  : 'Les gagnants n’ont pas pu être chargés. Réessayez dans un instant.'}
              </p>
            </Panel>
          )}

          {state.tab === 'collections' && collections.data && (
            <CollectionsTab data={collections.data} state={state} update={update} config={config} query={query} />
          )}
          {state.tab === 'lottery' && lottery.data && (
            <LotteryTab data={lottery.data} state={state} update={update} config={config} onSaved={() => lottery.refetch()} />
          )}

          {active.isPending && !active.data && <Panel title="Chargement…">{null}</Panel>}

          {active.data && total > 0 && (
            <div className="mono-pager">
              <span className="mono-note">
                {fmtInt(Math.min((state.page - 1) * PAGE_SIZE + 1, total))}–{fmtInt(Math.min(state.page * PAGE_SIZE, total))} sur {fmtInt(total)}
              </span>
              <div className="mono-pager-buttons">
                <button type="button" className="mono-button" disabled={state.page <= 1} onClick={() => update({ page: state.page - 1 })}>
                  Précédent
                </button>
                <button type="button" className="mono-button" disabled={state.page >= pages} onClick={() => update({ page: state.page + 1 })}>
                  Suivant
                </button>
              </div>
            </div>
          )}

          <div className="mono-foot">
            Données en direct du service des lots · contient le nom des gagnants des tirages — donnée personnelle, à ne pas diffuser hors équipe
          </div>
        </main>
      </div>
    </div>
  );
}

type State = {
  tab: WinnersTab;
  page: number;
  status: string;
  collection: string | null;
  kind: string | null;
  from: string | null;
  to: string | null;
};
type Update = (next: Partial<State>) => void;

function CollectionsTab({
  data,
  state,
  update,
  config,
  query,
}: {
  data: CollectionWinners;
  state: State;
  update: Update;
  config: DashboardConfig;
  query: WinnersQuery;
}) {
  const s = data.meta.summary;
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  async function onExport() {
    setExportError(null);
    setExporting('Préparation…');
    try {
      // Walks the pages behind the same filters the table is showing, so the
      // file matches what is on screen rather than the whole campaign.
      const { rows, truncated } = await getAllCollectionWinners(config.reportId, config.apiBaseUrl, query, (loaded, total) =>
        setExporting(`${fmtInt(loaded)} / ${fmtInt(total)}…`),
      );
      downloadCsv(csvFileName(state.from, state.to), buildWinnersCsv(rows));
      if (truncated) setExportError('Export limité aux 20 000 premières lignes — affinez le filtre par date.');
    } catch {
      setExportError('L’export n’a pas pu être généré. Réessayez dans un instant.');
    } finally {
      setExporting(null);
    }
  }
  return (
    <>
      <div className="mono-grid-4">
        <KpiTile
          label="Lots à remettre"
          value={fmtInt(s.total)}
          sub={s.lottery !== undefined ? `${fmtInt(s.collections ?? 0)} collections · ${fmtInt(s.lottery)} carte chance` : undefined}
        />
        <KpiTile label="En attente d’envoi" value={fmtInt(s.awaiting)} />
        <KpiTile label="Envoyées pour attribution" value={fmtInt(s.reported)} />
        <KpiTile label="Coordonnées fournies" value={fmtInt(s.dataProvided)} sub="par le gagnant" />
      </div>
      <AllocationsPanel config={config} />
      <Panel
        title="Gagnants et lots à remettre"
        action={
          <div className="mono-filters">
            <label className="mono-date">
              <span>Du</span>
              <input type="date" value={state.from ?? ''} aria-label="Date de début" onChange={(e) => update({ from: e.target.value || null })} />
            </label>
            <label className="mono-date">
              <span>au</span>
              <input type="date" value={state.to ?? ''} aria-label="Date de fin" onChange={(e) => update({ to: e.target.value || null })} />
            </label>
            <select
              className="mono-select"
              aria-label="Filtrer par type de gain"
              value={state.kind ?? 'all'}
              onChange={(e) => update({ kind: e.target.value })}
            >
              <option value="all">Tous les gains</option>
              <option value="collections">Collections</option>
              <option value="lottery">Carte chance</option>
            </select>
            <select
              className="mono-select"
              aria-label="Filtrer par collection"
              value={state.collection ?? 'all'}
              onChange={(e) => update({ collection: e.target.value === 'all' ? null : e.target.value })}
            >
              <option value="all">Toutes les collections</option>
              {data.meta.collections.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className="mono-select" aria-label="Filtrer par statut" value={state.status} onChange={(e) => update({ status: e.target.value })}>
              {Object.entries(COLLECTION_STATUS_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
            <button type="button" className="mono-button" onClick={onExport} disabled={exporting !== null || s.total === 0}>
              {exporting ?? 'Exporter en CSV'}
            </button>
          </div>
        }
      >
        {exportError && (
          <p className="mono-note" role="alert" style={{ color: 'var(--mono-red)' }}>
            {exportError}
          </p>
        )}
        {data.data.length === 0 ? (
          <p className="mono-note">Aucune collection terminée ne correspond à ce filtre.</p>
        ) : (
          <div className="mono-table-wrap">
            <table className="mono-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Gain</th>
                  <th>ID membre</th>
                  <th>Lot</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.date)}</td>
                    <td>
                      <span className="mono-swatch-dot" style={{ background: r.color ?? '#999' }} aria-hidden="true" />
                      {r.collection}
                    </td>
                    <td className="mono-mono">{r.memberId ?? r.cardNumber ?? '—'}</td>
                    <td>{r.prize ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mono-foot">
          « Envoyé pour attribution » signifie que le gagnant figure dans un fichier de gagnants transmis. Le magasin attribué revient par le dépôt ci-dessus.
        </p>
      </Panel>
    </>
  );
}

function LotteryTab({
  data,
  state,
  update,
  config,
  onSaved,
}: {
  data: LotteryWinners;
  state: State;
  update: Update;
  config: DashboardConfig;
  onSaved: () => void;
}) {
  const s = data.meta.summary;
  const [naming, setNaming] = useState<LotteryWinner | null>(null);
  return (
    <>
      <div className="mono-grid-4">
        <KpiTile label="Tirages quotidiens" value={fmtInt(s.total)} sub={`${fmtInt(s.dailyPrizeEur)} € chacun`} />
        <KpiTile label="À valider" value={fmtInt(s.pending)} />
        <KpiTile label="Validés" value={fmtInt(s.approved)} />
        <KpiTile label="Tirage final" value={`${fmtInt(s.finalPrizeEur)} €`} sub={`le ${fmtDate(s.finalDrawDate)}`} />
      </div>
      <Panel
        title="Gagnants des tirages carte chance"
        action={
          <select className="mono-select" aria-label="Filtrer par statut" value={state.status} onChange={(e) => update({ status: e.target.value })}>
            {Object.entries(LOTTERY_STATUS_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        }
      >
        {data.data.length === 0 ? (
          <p className="mono-note">Aucun tirage ne correspond à ce filtre.</p>
        ) : (
          <div className="mono-table-wrap">
            <table className="mono-table">
              <thead>
                <tr>
                  <th>Date du tirage</th>
                  <th>Gagnant</th>
                  <th>ID membre</th>
                  <th>Lot</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.date)}</td>
                    <td>
                      {r.name ? (
                        r.name
                      ) : r.proposedName ? (
                        <span>
                          {r.proposedName} <span className="mono-pill grey">proposé</span>
                        </span>
                      ) : (
                        <span className="mono-note">Nom non communiqué</span>
                      )}
                    </td>
                    <td className="mono-mono">{r.memberId ?? '—'}</td>
                    <td>{fmtInt(r.prizeEur)} €</td>
                    <td>
                      <span className={`mono-pill ${r.status === 'approved' ? 'green' : 'amber'}`}>{LOTTERY_STATUS_LABELS[r.status]}</span>
                      {/* Only an unapproved winner can be given a name: once
                          validated the name is settled and the API refuses. */}
                      {r.status === 'pending' && !r.name && (
                        <button type="button" className="mono-button" onClick={() => setNaming(r)}>
                          {r.proposedName ? 'Modifier le nom' : 'Renseigner le nom'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mono-foot">
          Tirage quotidien de la carte chance. Un nom saisi ici est enregistré dans le portail et repris manuellement dans l’admin : le gagnant reste « à
          valider » jusque-là.
        </p>
      </Panel>
      {naming && (
        <WinnerNameDialog
          drawDate={fmtDate(naming.date)}
          memberId={naming.memberId}
          initialName={naming.proposedName}
          onClose={() => setNaming(null)}
          onSubmit={async (name) => {
            await proposeWinnerName(config.reportId, config.apiBaseUrl, naming.id, name);
            onSaved();
          }}
        />
      )}
    </>
  );
}

/** ISO → 08/09/2026. Dates arrive as timestamps or plain days. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}
